import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

interface NavigationMenuContextValue {
  /** Whether the navigation drawer is open. */
  isOpen: boolean;
  /** Open the navigation drawer. */
  open: () => void;
  /** Close the navigation drawer. */
  close: () => void;
}

const NavigationMenuContext = createContext<NavigationMenuContextValue | undefined>(undefined);

interface NavigationMenuProviderProps {
  children: ReactNode;
}

export function NavigationMenuProvider({ children }: NavigationMenuProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return <NavigationMenuContext.Provider value={value}>{children}</NavigationMenuContext.Provider>;
}

/**
 * Access the navigation menu controls. Tolerant of a missing provider: returns
 * `undefined` instead of throwing, because shared headers (e.g. `LayoutHeader`)
 * also render on desktop where this provider is not mounted.
 */
export function useNavigationMenu(): NavigationMenuContextValue | undefined {
  return useContext(NavigationMenuContext);
}
